<template>
  <div class="register-step">
    <div class="auth-tabs">
      <div class="auth-tab" @click="goToLogin">LOGIN</div>
      <div class="auth-tab active">REGISTER</div>
    </div>
    
    <div class="form-container">
      <div class="required-note">* Denotes required field</div>
      <div v-if="errors.apiError" class="error-message general-error">{{ errors.apiError }}</div>
      
      <!-- First Name -->
      <div class="form-group">
        <label for="firstName">First name*</label>
        <input 
          type="text"
          id="firstName"
          v-model="formValues.firstName"
          class="form-control"
          placeholder="First Name*"
          required
        >
        <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
      </div>
      
      <!-- Last Name -->
      <div class="form-group">
        <label for="lastName">Last name*</label>
        <input 
          type="text"
          id="lastName"
          v-model="formValues.lastName"
          class="form-control"
          placeholder="Last Name*"
          required
        >
        <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
      </div>
      
      <!-- Mobile Phone -->
      <div class="form-group">
        <label for="mobilePhone">Mobile Phone*</label>
        <input 
          type="tel"
          id="mobilePhone"
          v-model="formValues.mobilePhone"
          class="form-control"
          placeholder="Mobile Phone*"
          required
        >
        <div v-if="errors.mobilePhone" class="error-message">{{ errors.mobilePhone }}</div>
      </div>
      
      <!-- Email -->
      <div class="form-group">
        <label for="email">Email*</label>
        <input 
          type="email"
          id="email"
          v-model="formValues.email"
          class="form-control"
          placeholder="email@example.com"
          required
        >
        <div v-if="errors.email" class="error-message">{{ errors.email }}</div>
      </div>
      
      <!-- Birthdate -->
      <div class="form-group">
        <label for="birthdate">Birthdate*</label>
        <input 
          type="date"
          id="birthdate"
          v-model="formValues.birthdate"
          class="form-control"
          placeholder="YYYY-MM-DD"
          required
          max="{{ new Date().toISOString().split('T')[0] }}"
        >
        <div v-if="errors.birthdate" class="error-message">{{ errors.birthdate }}</div>
      </div>
      
      <!-- hCaptcha - TEMPORARILY COMMENTED OUT -->
      <!-- <div class="captcha-container">
        <HCaptcha
          ref="hcaptcha"
          :sitekey="hcaptchaSiteKey"
          :language="'en'"
          :theme="'light'"
          :size="'normal'"
          @verify="onCaptchaVerified"
          @expired="onCaptchaExpired"
          @error="onCaptchaError"
        />
        <div v-if="errors.captcha" class="error-message">{{ errors.captcha }}</div>
      </div> -->
      
      <!-- Continue Button -->
      <button 
        class="btn btn-continue"
        @click="handleContinue"
        :disabled="isSubmitting"
      >
        <template v-if="isSubmitting">
          <v-progress-circular indeterminate color="white" size="20" class="spinner" />
        </template>
        <template v-else>
          Continue <v-icon>mdi-arrow-right</v-icon>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
// import HCaptcha from '@hcaptcha/vue3-hcaptcha'; // TEMPORARILY COMMENTED OUT
import apiService from '@/services/apiService'; // Import apiService
import { saveAuthToken, removeAuthToken, removeRefreshToken } from '@/utils/auth';

export default {
  name: 'RegisterStep1',
  components: {
    // HCaptcha // TEMPORARILY COMMENTED OUT
  },
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  mounted() {
    // Clear any existing tokens when user enters registration
    removeAuthToken();
    removeRefreshToken();
    this.$store.commit('CLEAR_AUTH');
  },
  data() {
    return {
      formValues: {
        firstName: this.formData.firstName || '',
        lastName: this.formData.lastName || '',
        mobilePhone: this.formData.mobilePhone || '', // Ensure key matches backend (phone_number)
        email: this.formData.email || '',
        birthdate: this.formData.birthdate || ''
        // agreeToTerms removed
      },
      // hcaptchaSiteKey: process.env.VUE_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001', // TEMPORARILY COMMENTED OUT
      // hcaptchaToken: null, // TEMPORARILY COMMENTED OUT
      errors: {
        firstName: '',
        lastName: '',
        mobilePhone: '',
        email: '',
        birthdate: '',
        // agreeToTerms removed
        // captcha: '', // TEMPORARILY COMMENTED OUT
        apiError: '' // Add field for general API errors
      },
      isSubmitting: false
    };
  },
  methods: {
    goToLogin() {
      this.$router.push({ name: 'login' });
    },
    // TEMPORARILY COMMENTED OUT - hCaptcha methods
    // onCaptchaVerified(token) {
    //   this.hcaptchaToken = token;
    //   this.errors.captcha = '';
    //   this.errors.apiError = ''; // Clear API error on new captcha
    // },
    // onCaptchaExpired() {
    //   this.hcaptchaToken = null;
    //   this.errors.captcha = 'Captcha expired. Please verify again.';
    // },
    // onCaptchaError(error) {
    //   console.error('hCaptcha error:', error);
    //   this.hcaptchaToken = null;
    //   this.errors.captcha = 'Error loading captcha. Please try again.';
    // },
    // resetCaptcha() {
    //   if (this.$refs.hcaptcha) {
    //     this.$refs.hcaptcha.reset();
    //   }
    //   this.hcaptchaToken = null;
    // },
    validateForm() {
      let isValid = true;
      this.errors = {
        firstName: '',
        lastName: '',
        mobilePhone: '',
        email: '',
        birthdate: '',
        // agreeToTerms removed
        // captcha: '', // TEMPORARILY COMMENTED OUT
        apiError: '' // Reset API error on validation
      };
      
      // First name validation
      if (!this.formValues.firstName.trim()) {
        this.errors.firstName = 'First name is required';
        isValid = false;
      }
      
      // Last name validation
      if (!this.formValues.lastName.trim()) {
        this.errors.lastName = 'Last name is required';
        isValid = false;
      }
      
      // Mobile phone validation
      if (!this.formValues.mobilePhone.trim()) {
        this.errors.mobilePhone = 'Mobile phone is required';
        isValid = false;
      }
      
      // Email validation
      if (!this.formValues.email.trim()) {
        this.errors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formValues.email)) {
        this.errors.email = 'Please enter a valid email address';
        isValid = false;
      }
      
      // Birthdate validation
      if (!this.formValues.birthdate.trim()) {
        this.errors.birthdate = 'Birthdate is required';
        isValid = false;
      }
      
      // TEMPORARILY COMMENTED OUT - hCaptcha validation
      // if (!this.hcaptchaToken) {
      //   this.errors.captcha = 'Please complete the captcha verification';
      //   isValid = false;
      // }
      
      // Terms agreement validation removed
      
      return isValid;
    },
    async handleContinue() {
      if (this.validateForm()) {
        this.isSubmitting = true;
        this.errors.apiError = '';

        const payload = {
          first_name: this.formValues.firstName,
          last_name: this.formValues.lastName,
          mobile_phone: this.formValues.mobilePhone,
          email: this.formValues.email,
          birthdate: this.formValues.birthdate,
          // hcaptcha_response: this.hcaptchaToken // TEMPORARILY COMMENTED OUT
        };

        try {
          const response = await apiService.registerStep1(payload);
          console.log('Registration Step 1 Success:', response.data);
          
          console.log('Registration response data:', response.data);
          if (response.data.token) {
            console.log('Token received, saving to localStorage:', response.data.token);
            saveAuthToken(response.data.token);
            this.$store.commit('SET_TOKEN', response.data.token);
            console.log('Token saved successfully.');
          } else {
            console.warn('No token received in response.');
          }

          // Update parent form data
          this.$emit('update:form-data', {
            email: this.formValues.email,
            firstName: this.formValues.firstName,
            lastName: this.formValues.lastName,
            mobilePhone: this.formValues.mobilePhone,
            birthdate: this.formValues.birthdate
          });

          // Proceed to next step
          this.$emit('next-step');
        } catch (error) {
          console.error('Registration Step 1 Failed:', error.response || error.message);
          if (error.response && error.response.data) {
            const data = error.response.data;

            // Field-level validation errors
            if (data.errors && typeof data.errors === 'object') {
              // Reset
              Object.assign(this.errors, {
                firstName: '',
                lastName: '',
                mobilePhone: '',
                email: '',
                birthdate: '',
                // agreeToTerms removed
                // captcha: '', // TEMPORARILY COMMENTED OUT
                apiError: ''
              });

              for (const [field, messages] of Object.entries(data.errors)) {
                const msg = Array.isArray(messages) ? messages.join(', ') : messages;
                switch (field) {
                  case 'first_name':
                    this.errors.firstName = msg;
                    break;
                  case 'last_name':
                    this.errors.lastName = msg;
                    break;
                  case 'mobile_phone':
                    this.errors.mobilePhone = msg;
                    break;
                  case 'email':
                    this.errors.email = msg;
                    break;
                  case 'birthdate':
                    this.errors.birthdate = msg;
                    break;
                  // TEMPORARILY COMMENTED OUT - hcaptcha_response case
                  // case 'hcaptcha_response':
                  //   this.errors.captcha = msg;
                  //   this.resetCaptcha();
                  //   break;
                  default:
                    this.errors.apiError = msg;
                }
              }
            } else if (data.detail) {
              this.errors.apiError = data.detail;
            } else {
              this.errors.apiError = 'Registration failed. Please try again.';
            }
          } else {
            this.errors.apiError = 'Network error. Please try again.';
          }
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  }
};
</script>

<style scoped>
.register-step {
  width: 100%;
}

.auth-tabs {
  display: flex;
  width: 100%;
}

.auth-tab {
  flex: 1;
  padding: 15px;
  text-align: center;
  background-color: #f1f5f9;
  color: #333;
  font-weight: bold;
  cursor: pointer;
}

.auth-tab.active {
  background-color: white;
}

.form-container {
  padding: 30px;
}

.required-note {
  font-size: 12px;
  color: #666;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.error-message {
  color: #e94057;
  font-size: 12px;
  margin-top: 5px;
}

.captcha-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.checkbox-container {
  display: flex;
  align-items: center;
}

.checkbox-container input {
  margin-right: 10px;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.terms-link {
  color: #4A6282;
  text-decoration: none;
}

.terms-link:hover {
  text-decoration: underline;
}

.btn-continue {
  width: 100%;
  padding: 15px;
  font-size: 16px;
  color: white;
  background-color: #4A6282;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-continue:hover {
  background-color: #3d5270;
}

.btn-continue:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.general-error {
  text-align: center;
}

.spinner {
  margin: 0;
}
</style> 